import { useState, useEffect, useMemo } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { toast } from "../../../../components/ui/use-toast"
import { Button } from "../../../../components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../../components/ui/alert-dialog"
import { Download, Trash2, Upload, Loader2 } from "lucide-react"
import { format } from "date-fns"
import { capitalCase } from "change-case"
import { PromotionFile, usePromotionalContextAPI } from "../../promo_creation/hooks/use_promotional_context_api"

interface PromotionFilesProps {
  promotionId: string
  className?: string
  onFilesChange?: () => void
}

function handleViewS3Uri(s3_uri: string) {
  return function handleClick(e: MouseEvent) {
    e.preventDefault()
    e.stopImmediatePropagation()
    window.open(`/api/_s3?s3_uri=${encodeURIComponent(s3_uri)}`, "_blank")
  }
}

function DeleteFileDialog({ file, promotionId, onDelete, deletePromotionFile }: { file: PromotionFile; promotionId: string; onDelete: () => void; deletePromotionFile: (promotionId: string, s3Uri: string) => Promise<boolean> }) {
  async function handleConfirmDelete() {
    try {
      await deletePromotionFile(promotionId, file.s3_uri)
      onDelete()
    } catch (error) {
      console.error("Error deleting file:", error)
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          onClick={e => e.stopPropagation()}
          class="p-1.5 rounded-full transition-colors cursor-pointer hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the file.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e: Event) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: Event) => {
              e.stopPropagation()
              handleConfirmDelete()
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function PromotionFiles({ promotionId, className, onFilesChange }: PromotionFilesProps) {
  const { uploadPromotionFiles, listPromotionFiles, deletePromotionFile } = usePromotionalContextAPI()
  const [files, setFiles] = useState<PromotionFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  async function loadFiles() {
    if (!promotionId) return
    setLoading(true)
    try {
      const fileList = await listPromotionFiles(promotionId)
      setFiles(fileList)
      onFilesChange?.()
    } catch (error) {
      console.error("Error loading files:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFiles()
  }, [promotionId])

  async function handleUpload(uploadedFiles: File[]) {
    if (!promotionId || uploadedFiles.length === 0) return
    setUploading(true)
    try {
      const newFiles = await uploadPromotionFiles(promotionId, uploadedFiles)
      if (newFiles.length > 0) {
        await loadFiles()
      }
    } catch (error) {
      console.error("Error uploading files:", error)
    } finally {
      setUploading(false)
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleUpload,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
      return dateB - dateA
    })
  }, [files])

  return (
    <div
      {...(getRootProps() as any)}
      className={`relative ${className || ""}`}
    >
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-primary/10 rounded-lg">
          <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-primary">
            <p className="text-primary font-medium">Drop files to upload</p>
          </div>
        </div>
      )}
      <div>
        <div class="flex justify-between items-center my-4">
          <h3 class="text-lg font-semibold">Files</h3>
          <Button
            onClick={open}
            disabled={uploading}
            class="flex items-center"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </div>
        {loading ? (
          <div class="w-full flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : sortedFiles.length === 0 ? (
          <div class="text-center text-gray-500 py-8">
            <p class="text-sm">No files uploaded yet.</p>
            <p class="text-xs mt-2">Click Upload or drag and drop files here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 my-4">
            {sortedFiles.map((file) => (
              <div
                key={file.id}
                class="flex gap-2 items-center p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors w-full cursor-pointer overflow-x-hidden"
                onClick={handleViewS3Uri(file.s3_uri)}
              >
                <Download className="h-4 w-4 flex-shrink-0 text-green-500 hover:text-green-600" />
                <div class="grow">
                  <span class="select-text py-1 text-nowrap pr-2 -ml-1 pl-1">
                    {file.name}
                  </span>
                  <div class="flex justify-between">
                    <span class="text-gray-500 text-sm">
                      {file.actor_name && capitalCase(file.actor_name)}
                    </span>
                    <span class="text-gray-500 text-sm text-nowrap">
                      {file.created_at && format(new Date(file.created_at), "PP")}
                    </span>
                  </div>
                </div>
                <DeleteFileDialog
                  file={file}
                  promotionId={promotionId}
                  onDelete={loadFiles}
                  deletePromotionFile={deletePromotionFile}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
